// Simple image URL utility
// This ensures images work on both local and production servers

/**
 * Get the full image URL for an uploaded image
 * @param {string} filename - The image filename from the database
 * @returns {string} - The full image URL
 */
export const getImageUrl = (filename) => {
  if (!filename) return '';
  
  // Debug logging
  console.log('🖼️ Image URL processing:', {
    original: filename,
    hostname: window.location.hostname,
    fullUrl: window.location.href
  });
  
  // If it's already a full URL, return as is
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    console.log('  → Full URL detected, returning as is');
    return filename;
  }
  
  // Helper function to detect weekly highlight images
  const isWeeklyHighlightImage = (path) => {
    // Check if it's a weekly highlight image by filename pattern
    const filenameOnly = path.split('/').pop();
    return filenameOnly && (
      filenameOnly.includes('weekly-highlights-') ||
      filenameOnly.includes('cover-image-') ||
      filenameOnly.includes('social-image-') ||
      filenameOnly.includes('gallery-image-')
    );
  };
  
  // For localhost, always construct the full backend URL
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('  → Localhost detected, constructing backend URL');
    
    // Handle weekly highlight images specifically
    if (isWeeklyHighlightImage(filename)) {
      console.log('  → Weekly highlight image detected, using weekly-highlights subdirectory');
      // Extract just the filename from the path
      const filenameOnly = filename.split('/').pop();
      const fullUrl = `http://localhost:5000/uploads/weekly-highlights/${filenameOnly}`;
      console.log('  → Constructed weekly highlight URL:', fullUrl);
      return fullUrl;
    }
    
    // Remove leading slash if present and construct full backend URL
    const cleanPath = filename.startsWith('/') ? filename.substring(1) : filename;
    const fullUrl = `http://localhost:5000/${cleanPath}`;
    console.log('  → Constructed standard URL:', fullUrl);
    return fullUrl;
  }
  
  // If it's already a relative URL starting with /uploads, return as is (for production)
  if (filename.startsWith('/uploads/')) {
    console.log('  → Relative URL detected, returning as is');
    return filename;
  }
  
  // If it starts with uploads/ (without leading slash), add the slash
  if (filename.startsWith('uploads/')) {
    console.log('  → uploads/ detected, adding leading slash');
    return `/${filename}`;
  }
  
  // For production domains, use relative URLs (served directly by NGINX)
  if (window.location.hostname === 'publications.dbohra.com' || 
      window.location.hostname === 'publications.dbdea.org') {
    console.log('  → Production domain detected, using relative URL (NGINX served)');
    
    // Handle weekly highlight images specifically for production
    if (isWeeklyHighlightImage(filename)) {
      console.log('  → Weekly highlight image detected for production, using weekly-highlights subdirectory');
      const filenameOnly = filename.split('/').pop();
      const fullUrl = `/uploads/weekly-highlights/${filenameOnly}`;
      console.log('  → Constructed production weekly highlight URL:', fullUrl);
      return fullUrl;
    }
    
    // Ensure we have the correct format for production
    const cleanPath = filename.startsWith('/') ? filename : `/${filename}`;
    console.log('  → Constructed production standard URL:', cleanPath);
    return cleanPath;
  }
  
  // Fallback for other localhost scenarios
  console.log('  → Other localhost scenario, using backend server URL');
  
  // Handle weekly highlight images specifically
  if (isWeeklyHighlightImage(filename)) {
    console.log('  → Weekly highlight image detected in fallback, using weekly-highlights subdirectory');
    const filenameOnly = filename.split('/').pop();
    const fullUrl = `http://localhost:5000/uploads/weekly-highlights/${filenameOnly}`;
    console.log('  → Constructed fallback weekly highlight URL:', fullUrl);
    return fullUrl;
  }
  
  const cleanPath = filename.startsWith('/') ? filename.substring(1) : filename;
  const fullUrl = `http://localhost:5000/${cleanPath}`;
  console.log('  → Constructed fallback standard URL:', fullUrl);
  return fullUrl;
};

/**
 * Get the image URL for display in img tags
 * @param {string} filename - The image filename from the database
 * @returns {string} - The image URL for display
 */
export const getDisplayImageUrl = (filename) => {
  return getImageUrl(filename);
};

/**
 * Check if an image exists
 * @param {string} filename - The image filename
 * @returns {boolean} - True if image exists
 */
export const imageExists = (filename) => {
  return !!filename && filename.trim() !== '';
};

/**
 * Get the full image URL specifically for weekly highlight images
 * @param {string} filename - The image filename from the database
 * @returns {string} - The full image URL for weekly highlight images
 */
export const getWeeklyHighlightImageUrl = (filename) => {
  if (!filename) return '';
  
  console.log('📰 Weekly Highlight Image URL processing:', {
    original: filename,
    hostname: window.location.hostname
  });
  
  // If it's already a full URL, return as is
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    console.log('  → Full URL detected, returning as is');
    return filename;
  }
  
  // Extract just the filename from the path
  const filenameOnly = filename.split('/').pop();
  
  // Check if the filename indicates it's a weekly highlight image
  const isWeeklyHighlightImage = filenameOnly && (
    filenameOnly.includes('weekly-highlights-') ||
    filenameOnly.includes('cover-image-') ||
    filenameOnly.includes('social-image-') ||
    filenameOnly.includes('gallery-image-')
  );
  
  if (!isWeeklyHighlightImage) {
    console.log('  → Not a weekly highlight image, using standard path');
    // For localhost, construct the full backend URL
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return `http://localhost:5000${filename}`;
    }
    // For production, return as is
    return filename;
  }
  
  // For localhost, construct the full backend URL with weekly-highlights subdirectory
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('  → Localhost detected, using weekly-highlights subdirectory');
    const fullUrl = `http://localhost:5000/uploads/weekly-highlights/${filenameOnly}`;
    console.log('  → Constructed URL:', fullUrl);
    return fullUrl;
  }
  
  // For production domains, use relative URLs with weekly-highlights subdirectory
  if (window.location.hostname === 'publications.dbohra.com' || 
      window.location.hostname === 'publications.dbdea.org') {
    console.log('  → Production domain detected, using weekly-highlights subdirectory');
    return `/uploads/weekly-highlights/${filenameOnly}`;
  }
  
  // Fallback for other scenarios
  console.log('  → Fallback scenario, using weekly-highlights subdirectory');
  return `http://localhost:5000/uploads/weekly-highlights/${filenameOnly}`;
};

/**
 * Get the full PDF URL for an uploaded PDF document
 * @param {string} filename - The PDF filename from the database
 * @returns {string} - The full PDF URL
 */
export const getPdfUrl = (filename) => {
  if (!filename) return '';
  
  // Debug logging
  console.log('📄 PDF URL processing:', {
    original: filename,
    hostname: window.location.hostname,
    fullUrl: window.location.href
  });
  
  // If it's already a full URL, return as is
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    console.log('  → Full URL detected, returning as is');
    return filename;
  }
  
  // For localhost, always construct the full backend URL
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('  → Localhost detected, constructing backend URL');
    
    // Remove leading slash if present and construct full backend URL
    const cleanPath = filename.startsWith('/') ? filename.substring(1) : filename;
    const fullUrl = `http://localhost:5000/${cleanPath}`;
    console.log('  → Constructed standard URL:', fullUrl);
    return fullUrl;
  }
  
  // If it's already a relative URL starting with /uploads, return as is (for production)
  if (filename.startsWith('/uploads/')) {
    console.log('  → Relative URL detected, returning as is');
    return filename;
  }
  
  // If it starts with uploads/ (without leading slash), add the slash
  if (filename.startsWith('uploads/')) {
    console.log('  → uploads/ detected, adding leading slash');
    return `/${filename}`;
  }
  
  // For production domains, use relative URLs (served directly by NGINX)
  if (window.location.hostname === 'publications.dbohra.com' || 
      window.location.hostname === 'publications.dbdea.org') {
    console.log('  → Production domain detected, using relative URL (NGINX served)');
    
    // Ensure we have the correct format for production
    const cleanPath = filename.startsWith('/') ? filename : `/${filename}`;
    console.log('  → Constructed production standard URL:', cleanPath);
    return cleanPath;
  }
  
  // Fallback for other localhost scenarios
  console.log('  → Other localhost scenario, using backend server URL');
  
  const cleanPath = filename.startsWith('/') ? filename.substring(1) : filename;
  const fullUrl = `http://localhost:5000/${cleanPath}`;
  console.log('  → Constructed fallback standard URL:', fullUrl);
  return fullUrl;
};

/**
 * Safely parse JSON data that might be a string or already an object
 * This handles the inconsistency between local and production environments
 * @param {string|object|array} data - The data to parse
 * @param {*} defaultValue - Default value to return if parsing fails
 * @returns {*} - Parsed data or default value
 */
export const safeJsonParse = (data, defaultValue = null) => {
  // If data is null, undefined, or empty string, return default
  if (data === null || data === undefined || data === '') {
    return defaultValue;
  }
  
  // If data is already an object or array, return as is
  if (typeof data === 'object') {
    return data;
  }
  
  // If data is a string, try to parse it
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed;
    } catch (error) {
      console.warn('Failed to parse JSON data:', { data, error: error.message });
      return defaultValue;
    }
  }
  
  // For any other type, return default
  return defaultValue;
};
